import { Injectable, inject, effect, signal } from '@angular/core';
import {
  CustomLogTheme,
  LogThemeConfig,
  getAllThemes,
  getThemesByMode,
  getThemeById
} from '../models/log-theme.model';

import {ThemeService} from '@app/core/services/theme.service';

/**
 * Service for managing log table themes
 * Automatically syncs with main theme (light/dark) and persists user selection
 */
@Injectable({
  providedIn: 'root'
})
export class LogThemeService {
  private readonly STORAGE_KEY_LIGHT = 'fluxtail-log-theme-light';
  private readonly STORAGE_KEY_DARK = 'fluxtail-log-theme-dark';
  private themeService = inject(ThemeService);

  // Current theme as a signal for reactivity
  private currentThemeSignal = signal<LogThemeConfig | null>(null);

  // Stored theme IDs for each mode (for dropdown bindings)
  private storedLightThemeId = signal<string>('fluxtail-light');
  private storedDarkThemeId = signal<string>('fluxtail-dark');

  constructor() {
    this.loadStoredThemeIds();

    this.syncTheme();

    effect(() => {
      this.themeService.appliedTheme();
      this.storedLightThemeId();
      this.storedDarkThemeId();
      this.syncTheme();
    }, { allowSignalWrites: true });
  }

  private loadStoredThemeIds(): void {
    const lightId = this.getStoredThemeId('light');
    const darkId = this.getStoredThemeId('dark');
    if (lightId) this.storedLightThemeId.set(lightId);
    if (darkId) this.storedDarkThemeId.set(darkId);
  }

  private syncTheme(): void {
    const mode = this.themeService.appliedTheme();
    const currentTheme = this.currentThemeSignal();
    const desiredThemeId = mode === 'light' ? this.storedLightThemeId() : this.storedDarkThemeId();

    if (currentTheme?.id === desiredThemeId) {
      return;
    }

    const theme = getThemeById(desiredThemeId);
    if (theme) {
      this.applyTheme(theme);
      return;
    }

    this.setDefaultThemeForMode(mode);
  }

  /**
   * Get all available themes
   */
  getAllThemes(): LogThemeConfig[] {
    return getAllThemes();
  }

  /**
   * Get themes for specific mode (light/dark)
   */
  getThemesForMode(mode: 'light' | 'dark'): LogThemeConfig[] {
    return getThemesByMode(mode);
  }

  /**
   * Get themes for current main theme mode
   */
  getThemesForCurrentMode(): LogThemeConfig[] {
    const mode = this.themeService.appliedTheme();
    return this.getThemesForMode(mode);
  }

  /**
   * Get currently applied theme
   */
  getCurrentTheme(): LogThemeConfig | null {
    return this.currentThemeSignal();
  }

  refreshCurrentTheme(): void {
    const currentTheme = this.currentThemeSignal();
    if (currentTheme) {
      this.applyTheme(currentTheme);
      return;
    }
    this.syncTheme();
  }

  /**
   * Get stored light theme ID (reactive)
   */
  getStoredLightThemeId(): string {
    return this.storedLightThemeId();
  }

  /**
   * Get stored dark theme ID (reactive)
   */
  getStoredDarkThemeId(): string {
    return this.storedDarkThemeId();
  }

  setThemePair(lightThemeId: string, darkThemeId: string): boolean {
    const lightTheme = getThemeById(lightThemeId);
    const darkTheme = getThemeById(darkThemeId);
    if (!lightTheme || lightTheme.mode !== 'light' || !darkTheme || darkTheme.mode !== 'dark') {
      return false;
    }

    this.storeThemeId(lightThemeId, 'light');
    this.storeThemeId(darkThemeId, 'dark');
    this.syncTheme();
    return true;
  }

  /**
   * Set theme by ID
   * Stores preference for that theme's mode
   * If mode matches current main theme, applies immediately
   */
  setTheme(themeId: string): boolean {
    const theme = getThemeById(themeId);
    if (!theme) {
      console.warn(`Log theme not found: ${themeId}`);
      return false;
    }

    // Always store the preference for this mode
    this.storeThemeId(theme.id, theme.mode);

    // Only apply if the theme mode matches current main theme
    const activeMode = this.themeService.appliedTheme();
    if (theme.mode === activeMode) {
      this.applyTheme(theme);
    }

    return true;
  }

  /**
   * Apply theme to DOM
   */
  private applyTheme(theme: LogThemeConfig): void {
    const customTheme = new CustomLogTheme(theme);
    customTheme.apply();
    this.currentThemeSignal.set(theme);
  }

  /**
   * Set default theme for given mode
   */
  private setDefaultThemeForMode(mode: 'light' | 'dark'): void {
    const defaultThemeId = mode === 'dark' ? 'fluxtail-dark' : 'fluxtail-light';
    const theme = getThemeById(defaultThemeId);

    if (theme) {
      this.storeThemeId(theme.id, mode);
      this.applyTheme(theme);
    }
  }

  private getStoredThemeId(mode: 'light' | 'dark'): string | null {
    if (typeof localStorage === 'undefined') return null;
    const key = mode === 'light' ? this.STORAGE_KEY_LIGHT : this.STORAGE_KEY_DARK;
    return localStorage.getItem(key);
  }

  /**
   * Store theme ID to localStorage for specific mode
   */
  private storeThemeId(themeId: string, mode: 'light' | 'dark'): void {
    if (typeof localStorage === 'undefined') return;
    const key = mode === 'light' ? this.STORAGE_KEY_LIGHT : this.STORAGE_KEY_DARK;
    localStorage.setItem(key, themeId);

    // Update the signal so UI updates reactively
    if (mode === 'light') {
      this.storedLightThemeId.set(themeId);
    } else {
      this.storedDarkThemeId.set(themeId);
    }
  }

  /**
   * Clear stored theme (reset to default)
   */
  resetToDefault(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.STORAGE_KEY_LIGHT);
      localStorage.removeItem(this.STORAGE_KEY_DARK);
    }
    this.storedLightThemeId.set('fluxtail-light');
    this.storedDarkThemeId.set('fluxtail-dark');
    this.syncTheme();
  }
}
